import React, { useEffect, useState } from 'react'
import { ConfigProvider } from 'antd'
import { CustomisationContext, Theme, Config } from './CustomisationContext'
import { DEFAULT_THEME, DEFAULT_CONFIG } from './constants'
import { updateTranslations } from '../i18n'

export const CustomisationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME)
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchConfig = async (name: string) => {
      const response = await fetch('/config/' + name)
      if (!response.ok) {
        throw new Error(
          `Fetch error for ${name}: ${response.status} ${response.statusText}`
        )
      }
      return response.json()
    }

    const fetchData = async () => {
      try {
        const configData = await fetchConfig('context.json')
        const accountData = await fetchConfig('account.json')

        const getRedirectUri = () => {
          const urlParams = new URLSearchParams(window.location.search)
          const hashParams = new URLSearchParams(
            window.location.hash.split('?')[1]
          )

          let redirectUri = urlParams.get('redirect_uri')

          if (!redirectUri) {
            redirectUri = hashParams.get('redirect_uri')
          }

          if (redirectUri) {
            try {
              const decoded = decodeURIComponent(redirectUri)
              return decoded
            } catch (error) {
              console.warn('Failed to decode redirect_uri:', error)
              return redirectUri
            }
          }

          return configData.config?.redirectUri || DEFAULT_CONFIG.redirectUri
        }

        const extractedRedirectUri = getRedirectUri()
        const apiTheme = accountData.theme || accountData
        const mappedTheme: Theme = {
          ...DEFAULT_THEME,
          ...apiTheme,
        }

        setTheme(mappedTheme)

        setConfig({
          ...(configData.config || DEFAULT_CONFIG),
          apiHost: configData.api_host || DEFAULT_CONFIG.apiHost,
          realm: configData.realm_name || DEFAULT_CONFIG.realm,
          redirectUri: extractedRedirectUri,
        })

        if (accountData.translations) {
          updateTranslations(accountData.translations)
        }
      } catch (error) {
        console.error('Failed to fetch customisation:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const value = {
    theme,
    config,
    setTheme,
    setConfig,
    apiHost: config.apiHost,
    realm: config.realm,
    redirectUri: config.redirectUri,
    loading,
  }

  const styles = `:root {
  --colorPrimary: ${theme.colorPrimary};
  --colorPrimaryHover: ${theme.colorPrimaryHover};
  --borderRadius: ${theme.borderRadius};
  --colorLink: ${theme.colorLink};
  --colorLinkHover: ${theme.colorLinkHover};
  --colorLinkActive: ${theme.colorLinkActive};
  --controlHeight: ${theme.controlHeight}px;
  --controlInteractiveSize: ${theme.controlInteractiveSize};
  --logoHeight: ${theme.logoHeight};
  --labelColor: ${theme.labelColor};
  --loginBGImage: ${theme.loginBGImage};
}`

  const antdTheme = {
    colorPrimary: theme.colorPrimary,
    colorLink: theme.colorLink,
    borderRadius: parseInt(theme.borderRadius) || 4,
    controlHeight: parseInt(theme.controlHeight) || 50,
    controlInteractiveSize: parseInt(theme.controlInteractiveSize) || 24,
  }

  return (
    <CustomisationContext.Provider value={value}>
      <style>{styles}</style>
      <ConfigProvider theme={{ token: antdTheme }}>{children}</ConfigProvider>
    </CustomisationContext.Provider>
  )
}